// @ts-nocheck
/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable no-undef */
import React, {
  useEffect, useRef, useState,
} from 'react';
import { Layer } from '@deck.gl/core';
import { GoogleMapsOverlay } from '@deck.gl/google-maps';
import { Loader as GoogleMapsLoader } from '@googlemaps/js-api-loader';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import { LocateControl } from '../../data-view/map/simple-control';
import Loader from '../loader';
import { useApp } from '../../hooks/use-app';
import { Extents, LatLng, defaultExtents } from '../../types/geo';
import { addInfoWindowElement, injectInMapSearchField } from './google-maps-helper';
import { MapPinMarker } from '../../record-viewer/record-map-pins';
import MapRecordCard from './map-record-card';

interface Props {
  mapExtents: Extents | null,
  allowAddingMarker?: boolean,
  onAddMarker?: (latlng: LatLng) => void,
  saveMapExtents?: (extents: Extents) => void,
  inMapAutoCompleteSearch?: boolean
  marker?: LatLng;
  markers?: MapPinMarker[];
  layers?: Readonly<Layer[]>;
  onClickPin?: (record: Record<string, any>) => void;
}

const defaultMapOptions = {
  center: { lat: 0, lng: 0 },
  zoom: 1.5,
  cursor: '',
};

const StyledBox = styled(Box)({
  flexGrow: 1,
  width: '100%',
  height: '100%',
  minWidth: 100,
  minHeight: 100,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '8px',
});

// MapView uses deckgl with google maps to take advantage of hardware acceleration,
// and allow for more visualization types.
// Right now it does not have the same functionality as the editor/map/map-view component,
// but the intent is to progressively add functionality, until it can be swapped out.
// eslint-disable-next-line import/prefer-default-export
export const MapView: React.FC<Props> = React.memo(({
  mapExtents,
  allowAddingMarker = false,
  onAddMarker,
  saveMapExtents,
  inMapAutoCompleteSearch = false,
  marker,
  markers,
  layers,
  onClickPin,
}: Props) => {
  const app = useApp();
  const mapRef = useRef(null);
  const extents = mapExtents || defaultExtents;
  const [googleMap, setGoogleMap] = useState<google.maps.Map | undefined>(undefined);
  const [googleMapOverlay, setGoogleMapOverlay] = useState<any | undefined>(undefined);
  const [googleMapsReady, setGoogleMapsReady] = useState(false);
  const [stateMarkers] = useState<google.maps.Marker[]>([]);
  const [singleMarker, setSingleMarker] = useState<google.maps.Marker | undefined>(undefined);

  useEffect(() => {
    loadMaps();
  }, []);

  useEffect(() => {
    if (googleMap && !googleMapOverlay) {
      setGoogleMapsReady(false);
      setGoogleMapOverlay(new GoogleMapsOverlay({
        layers,
      }));
    }
  }, [googleMap]);

  useEffect(() => {
    setGoogleMapsReady(false);
    if (googleMapOverlay && googleMap) {
      googleMapOverlay.setMap(googleMap);
    }
  }, [googleMapOverlay]);

  useEffect(() => {
    if (layers && googleMapOverlay) {
      googleMapOverlay.setProps({
        layers,
      });
      googleMapOverlay.setMap(googleMap);
    }
    setGoogleMapsReady(true);
  }, [layers]);

  useEffect(() => {
    if (googleMap) {
      google.maps.event.addListenerOnce(googleMap, 'tilesloaded', () => {
        if (inMapAutoCompleteSearch) {
          injectInMapSearchField(googleMap);
        }
        setGoogleMapsReady(true);
      });
    }
  }, [googleMap]);

  // handles changes in multiple markers
  useEffect(() => {
    if (markers && googleMapsReady && googleMap) {
      stateMarkers.forEach((marker) => marker.setMap(null));
      markers.forEach((point) => {
        const newMarker = new google.maps.Marker({
          position: point.latlng,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 7,
            fillColor: 'transparent',
            fillOpacity: 1,
            strokeWeight: 0,
          },
          map: googleMap,
        });
        newMarker.setPosition(point.latlng);
        addInfoWindowElement(
          newMarker,
          new google.maps.InfoWindow({
            content: `<div id="${point.record.id}"></div>`,
          }),
          <MapRecordCard
            onClickCard={(record) => (onClickPin ? onClickPin(record) : null)}
            record={point.record}
            queryColumnsForm={point.queryColumnsForm}
            systemType={point.systemType}
          />,
          point.record.id,
          googleMap,
        );
        stateMarkers.push(newMarker);
      });
    }
  }, [markers, googleMapsReady]);

  // handles changes in single marker
  useEffect(() => {
    if (googleMap && googleMapsReady && singleMarker && marker) {
      const validMarkerPoint = marker.lat !== 0 && marker.lng !== 0;
      if (validMarkerPoint) {
        singleMarker.setPosition(marker);
        googleMap.panTo(marker);
      }
    }
  }, [marker?.lat, marker?.lng, googleMapsReady, googleMap]);

  const loadMaps = async () => {
    try {
      await loadGoogleMaps({
        apiKey: app.globals.config.google_maps.api_key,
        version: app.globals.config.google_maps.version,
      });
      if (mapRef.current) {
        const gmap = await createMap(
          mapRef.current,
          extents,
          saveMapExtents,
        );
        const newMarker = initSingleMarker(gmap);
        setSingleMarker(newMarker);
        setGoogleMap(gmap);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const initSingleMarker = (map: google.maps.Map): google.maps.Marker | undefined => {
    if (map) {
      const newMarker = new google.maps.Marker({
        position: marker,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 7,
          fillColor: '#FF3B30',
          fillOpacity: 1,
          strokeWeight: 0,
        },
        map,
      });
      if (allowAddingMarker && onAddMarker) {
        google.maps.event.addListener(map, 'click', (e) => {
          newMarker.setPosition(e.latLng);
          map.panTo(e.latLng);
          onAddMarker?.({ lat: e.latLng.lat(), lng: e.latLng.lng() });
          saveMapExtents?.({ ...extents, zoom: map.getZoom() });
        });
      }
      return newMarker;
    }
    return undefined;
  };

  return (
    <>
      <div id="in-map-search-box" />
      <StyledBox data-testid="google-map-view" className="map-box" ref={mapRef}>
        <Loader partial />
      </StyledBox>
    </>
  );
});

function loadGoogleMaps({ apiKey, version }): Promise<typeof google> {
  const googleMapsLoader = new GoogleMapsLoader({ apiKey, version, libraries: ['places'] });
  return googleMapsLoader.load();
}

async function createMap(
  node: Element,
  extents: Extents,
  saveMapExtents?: (extents: Extents) => void,
) {
  const { default: GeolocationMarker } = import('../../data-view/map/geolocation-marker');
  const mapCenter = { lat: extents.latitude, lng: extents.longitude } || defaultMapOptions.center;
  const newMap = new google.maps.Map(node, {
    center: mapCenter,
    zoom: extents.zoom || defaultMapOptions.zoom,
    draggableCursor: defaultMapOptions.cursor,
    clickableIcons: false,
    disableDefaultUI: true,
    controlSize: 28,
    zoomControl: true,
    zoomControlOptions: {
      position: google.maps.ControlPosition.LEFT_TOP,
    },
    rotateControl: true,
    scaleControl: true,
  });

  let _watchingLocation = false;
  let _geolocationMarker = null;

  const locateButton = new LocateControl(() => {
    if (!('geolocation' in navigator)) {
      return;
    }

    if (_watchingLocation) {
      locateButton.setIcon(LocateControl.icon);
      _watchingLocation = false;

      if (_geolocationMarker) {
        _geolocationMarker.setMap(null);
        _geolocationMarker = null;
      }
    } else {
      locateButton.setIcon(LocateControl.iconActive);
      _watchingLocation = true;
      _geolocationMarker = new GeolocationMarker();

      google.maps.event.addListenerOnce(_geolocationMarker, 'position_changed', () => {
        const center = _geolocationMarker.getPosition();

        if (center) {
          newMap.setCenter(center);
        }
      });

      _geolocationMarker.setMap(newMap);
    }
  });
  if (saveMapExtents) {
    newMap.addListener('idle', () => {
      const newExtents = {
        ...extents,
        latitude: newMap.getCenter().lat(),
        longitude: newMap.getCenter().lng(),
        zoom: newMap.getZoom(),
      };
      saveMapExtents(newExtents);
    });
  }

  newMap.controls[google.maps.ControlPosition.LEFT_TOP].push(locateButton.element);
  return newMap;
}
